import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state/selectors';
import { UNKNOWN_DEPARTMENT_ID } from '../constants';
import { KEY_GET_DEPARTMENTS } from '../constants/keys';
import { API_GET_DEPARTMENTS } from '../constants/urls';
import { MOCK_RESPONSE_GET_DEPARTMENTS } from '../mocks';
import { ResponseGetDepartments } from '../types';

type CallParamsGetDepartments = {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

export type CallResponseGetDepartments = ResponseGetDepartments;

type CallErrorGetDepartments = GenericApiError;

type CallSelectorsGetDepartments = {
  data: ResponseGetDepartments['data'];
  totalItems: ResponseGetDepartments['totalItems'];
  hasUnknownDepartment: boolean;
  departmentsToShow: ResponseGetDepartments['data'];
  departmentsToShowCount: number;
};

export const getDepartmentsCall = createGeneralCall<
  CallParamsGetDepartments,
  CallResponseGetDepartments,
  CallErrorGetDepartments,
  CallSelectorsGetDepartments
>({
  id: KEY_GET_DEPARTMENTS,
  method: 'get',
  url: () => state => {
    const org = getCurrentOrganization(state);
    return API_GET_DEPARTMENTS(org);
  },
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_DEPARTMENTS : undefined,
  selectors: {
    data: ({ selectors }) => createSelector(selectors.response, res => res?.data || []),
    totalItems: ({ selectors }) => createSelector(selectors.response, res => res?.totalItems || 0),
    hasUnknownDepartment: ({ selectors }) =>
      createSelector(selectors.response, res =>
        (res?.data || []).some(d => d.id?.toLowerCase() === UNKNOWN_DEPARTMENT_ID),
      ),
    departmentsToShow: ({ selectors }) =>
      createSelector(selectors.response, res =>
        (res?.data || []).filter(d => d.id?.toLowerCase() !== UNKNOWN_DEPARTMENT_ID),
      ),
    departmentsToShowCount: ({ selectors }) =>
      createSelector(
        selectors.response,
        res => (res?.data || []).filter(d => d.id?.toLowerCase() !== UNKNOWN_DEPARTMENT_ID).length,
      ),
  },
});
